import React from "react";
import { Box } from "@mui/material";
import { voFloorDaypart, voFloorSceneTokens } from "./voFloorLiving";

const DUST = [
  { left: "22%", bottom: "34%", delay: "0s", dur: "9s" },
  { left: "36%", bottom: "46%", delay: "1.4s", dur: "11s" },
  { left: "51%", bottom: "40%", delay: "2.2s", dur: "8s" },
  { left: "64%", bottom: "50%", delay: "0.6s", dur: "10s" },
];

const SLATS = [0, 1, 2, 3, 10, 11, 12, 13];
const SHELF_ROWS = [148, 236, 324, 412, 500];
const BOOK_PALETTE_INDEX = [0, 1, 2, 3, 4, 5, 1, 0, 4, 2, 5, 3, 1, 4, 0, 2];

function shelfBooks(rowY) {
  const books = [];
  let x = 1508;
  let i = 0;
  while (x < 1868) {
    const w = 12 + ((i * 7) % 11);
    const h = 38 + ((i * 13) % 24);
    books.push({ x, y: rowY - h + 6, w, h, i: BOOK_PALETTE_INDEX[i % BOOK_PALETTE_INDEX.length] });
    x += w + 3;
    i += 1;
  }
  return books;
}

const LIBRARY_BOOKS = SHELF_ROWS.flatMap((y) => shelfBooks(y));

function FloorRoomSvg({ tokens, daypart }) {
  const id = `vo-room-${daypart}`;
  const evening = daypart === "evening";
  const morning = daypart === "morning";

  return (
    <svg
      viewBox="0 0 1920 900"
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
      aria-hidden
      style={{ position: "absolute", inset: 0, display: "block" }}
    >
      <defs>
        <linearGradient id={`${id}-ceiling`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.ceiling} />
          <stop offset="1" stopColor={tokens.wall} />
        </linearGradient>
        <linearGradient id={`${id}-wall`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.wall} />
          <stop offset="1" stopColor={tokens.wallDeep} />
        </linearGradient>
        <linearGradient id={`${id}-floor`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.floor} />
          <stop offset="1" stopColor={tokens.floorDeep} />
        </linearGradient>
        <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.glassTop} />
          <stop offset="1" stopColor={tokens.glassBot} />
        </linearGradient>
        <linearGradient id={`${id}-rug`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.rugInner} />
          <stop offset="1" stopColor={tokens.rug} />
        </linearGradient>
        <linearGradient id={`${id}-wainscot`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={tokens.wainscot} />
          <stop offset="1" stopColor={tokens.wainscotDeep} />
        </linearGradient>
        <radialGradient id={`${id}-pool`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={tokens.lampShade} stopOpacity="0.26" />
          <stop offset="1" stopColor={tokens.lampShade} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-shaft`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor={tokens.glassTop} stopOpacity="0.42" />
          <stop offset="1" stopColor={tokens.glassTop} stopOpacity="0" />
        </linearGradient>
        <pattern id={`${id}-herring`} width="36" height="20" patternUnits="userSpaceOnUse">
          <rect width="36" height="20" fill={tokens.floor} />
          <path d="M0 0 L18 20 M18 0 L36 20" stroke={tokens.floorLine} strokeOpacity="0.28" strokeWidth="1.2" />
        </pattern>
        <pattern id={`${id}-grain`} width="7" height="7" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill={tokens.trim} opacity="0.07" />
        </pattern>
        <clipPath id={`${id}-glass-clip`}>
          <rect x="36" y="48" width="700" height="520" rx="6" />
        </clipPath>
      </defs>

      <rect width="1920" height="900" fill={tokens.floorDeep} />
      <rect width="1920" height="56" fill={`url(#${id}-ceiling)`} />
      <rect y="40" width="1920" height="554" fill={`url(#${id}-wall)`} />
      <rect y="40" width="1920" height="554" fill={`url(#${id}-grain)`} />

      <g clipPath={`url(#${id}-glass-clip)`}>
        <rect x="36" y="48" width="700" height="520" fill={`url(#${id}-glass)`} />
        {morning ? (
          <>
            <circle cx="620" cy="150" r="54" fill="#fff3c4" />
            <circle cx="620" cy="150" r="86" fill="#fff3c4" opacity="0.22" />
          </>
        ) : null}
        {evening ? <circle cx="120" cy="120" r="20" fill="#eef2fa" opacity="0.88" /> : null}
        {!morning && !evening ? (
          <>
            <ellipse cx="220" cy="128" rx="90" ry="24" fill="#fff" opacity="0.22" />
            <ellipse cx="400" cy="108" rx="120" ry="28" fill="#fff" opacity="0.16" />
          </>
        ) : null}
        {[
          [56, 318, 78, 250],
          [142, 278, 96, 290],
          [246, 304, 70, 264],
          [324, 250, 118, 318],
          [450, 288, 84, 280],
          [542, 236, 110, 332],
          [660, 270, 86, 298],
        ].map(([x, y, w, h]) => (
          <rect key={`${x}-${w}`} x={x} y={y} width={w} height={h} fill={x % 20 > 10 ? tokens.city : tokens.cityFar} />
        ))}
        {evening
          ? [
              [156, 300],
              [176, 300],
              [156, 328],
              [338, 270],
              [362, 270],
              [338, 300],
              [560, 256],
              [586, 256],
              [560, 286],
              [586, 316],
              [676, 292],
            ].map(([x, y], i) => (
              <rect key={`${x}-${y}`} x={x} y={y} width="11" height="13" fill={tokens.cityLit} opacity={i % 2 ? 0.5 : 0.9}>
                <animate
                  attributeName="opacity"
                  values="0.28;0.95;0.4"
                  dur={`${2.5 + (i % 4) * 0.7}s`}
                  repeatCount="indefinite"
                />
              </rect>
            ))
          : null}
      </g>
      <rect x="28" y="40" width="716" height="536" rx="10" fill="none" stroke={tokens.trim} strokeWidth="16" />
      {[269, 502].map((x) => (
        <rect key={`vm-${x}`} x={x} y="48" width="10" height="520" fill={tokens.trim} />
      ))}
      {[218, 388].map((y) => (
        <rect key={`hm-${y}`} x="36" y={y} width="700" height="10" fill={tokens.trim} />
      ))}

      <polygon
        points="36,48 736,48 980,820 0,820"
        fill={`url(#${id}-shaft)`}
        opacity={evening ? 0.28 : 0.8}
      >
        <animate
          attributeName="opacity"
          values={evening ? "0.18;0.36;0.18" : "0.55;0.88;0.55"}
          dur="8s"
          repeatCount="indefinite"
        />
      </polygon>

      {SLATS.map((i) => (
        <rect
          key={`slat-${i}`}
          x={820 + i * 42}
          y={96}
          width="28"
          height="400"
          rx="3"
          fill={i % 2 ? tokens.wood : tokens.shelf}
        />
      ))}
      <rect x="988" y="128" width="268" height="300" rx="8" fill={tokens.trim} />
      <rect x="1000" y="140" width="244" height="276" rx="4" fill={tokens.wallDeep} />
      <rect x="1020" y="168" width="120" height="160" rx="3" fill={tokens.rug} opacity="0.8" />
      <rect x="1154" y="200" width="70" height="96" rx="3" fill={tokens.cityFar} opacity="0.8" />
      <ellipse cx="1140" cy="360" rx="70" ry="28" fill={tokens.lampShade} opacity="0.45" />
      <rect x="812" y="80" width="604" height="10" fill={tokens.trim} />
      <rect x="812" y="508" width="604" height="10" fill={tokens.trim} />

      <g>
        <rect x="1488" y="64" width="400" height="528" fill={tokens.shelf} />
        <rect x="1498" y="74" width="380" height="508" fill={tokens.wood} opacity="0.28" />
        {SHELF_ROWS.map((y) => (
          <rect key={`shelf-${y}`} x="1498" y={y} width="380" height="10" fill={tokens.trim} />
        ))}
        {LIBRARY_BOOKS.map((book) => (
          <rect
            key={`${book.x}-${book.y}`}
            x={book.x}
            y={book.y}
            width={book.w}
            height={book.h}
            rx="1.2"
            fill={tokens.books[book.i % tokens.books.length]}
          />
        ))}
      </g>

      <rect y="584" width="1920" height="16" fill={tokens.trim} />
      <rect y="500" width="1920" height="84" fill={`url(#${id}-wainscot)`} opacity="0.35" />
      <rect y="600" width="1920" height="300" fill={`url(#${id}-floor)`} />
      <rect y="600" width="1920" height="300" fill={`url(#${id}-herring)`} opacity="0.55" />

      <ellipse cx="980" cy="800" rx="620" ry="68" fill={`url(#${id}-rug)`} opacity="0.9" />
      <ellipse
        cx="980"
        cy="800"
        rx="580"
        ry="50"
        fill="none"
        stroke={tokens.rugInner}
        strokeOpacity="0.35"
        strokeWidth="8"
      />

      <rect x="56" y="528" width="680" height="60" rx="6" fill={tokens.wood} />
      <rect x="68" y="536" width="656" height="8" fill={tokens.lampMetal} opacity="0.28" />

      <g>
        <rect x="748" y="500" width="46" height="88" rx="6" fill={tokens.rugInner} />
        <path d="M768 500 C740 420 690 380 760 330 C800 360 792 430 778 500 Z" fill={tokens.plant} />
        <path d="M776 500 C830 410 900 370 820 320 C780 360 786 440 782 500 Z" fill={tokens.plantDeep} />
        <path d="M772 450 C720 400 710 340 778 300 C810 340 800 400 780 450 Z" fill={tokens.plant} />
        <path d="M780 460 C840 400 860 350 804 318 C776 356 782 414 784 460 Z" fill={tokens.plantDeep} />
      </g>

      <g>
        <line x1="860" y1="36" x2="1380" y2="36" stroke={tokens.lampMetal} strokeWidth="6" strokeLinecap="round" />
        <rect x="1040" y="36" width="8" height="28" fill={tokens.lampMetal} />
        <rect x="1180" y="36" width="8" height="28" fill={tokens.lampMetal} />
        <rect x="870" y="62" width="500" height="10" rx="4" fill={tokens.lampShade} />
        <ellipse cx="1120" cy="790" rx="340" ry="46" fill={`url(#${id}-pool)`}>
          <animate attributeName="opacity" values="0.6;1;0.6" dur="5s" repeatCount="indefinite" />
        </ellipse>
      </g>

      <g opacity={evening ? 1 : 0.2}>
        {[900, 1020, 1140, 1260].map((x) => (
          <circle key={x} cx={x} cy="92" r="7" fill={tokens.lampShade} />
        ))}
      </g>
    </svg>
  );
}

/**
 * Interior do piso: janela de escritório, parede de ripas e estante.
 * Os assentos ficam na frente, no tapete.
 */
export default function VoFloorScene({
  daypart: daypartProp,
  peopleCount: _peopleCount = 1,
  label = true,
  children,
}) {
  const daypart = daypartProp || voFloorDaypart();
  const tokens = voFloorSceneTokens(daypart);

  return (
    <Box
      data-vo-floor-scene={daypart}
      aria-hidden={!label}
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "16px",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
        background: tokens.floorDeep,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background: `${tokens.glow}, ${tokens.lamp}`,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          boxShadow: "inset 0 -90px 100px rgba(16,10,8,0.32), inset 0 30px 40px rgba(16,10,8,0.1)",
        },
      }}
    >
      <FloorRoomSvg tokens={tokens} daypart={daypart} />
      {DUST.map((spec, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: spec.left,
            bottom: spec.bottom,
            width: 3,
            height: 3,
            zIndex: 1,
            borderRadius: "50%",
            bgcolor: "rgba(255,236,200,0.4)",
            animation: `voDust ${spec.dur} ease-in-out ${spec.delay} infinite`,
            "@keyframes voDust": {
              "0%, 100%": { opacity: 0.1, transform: "translateY(0)" },
              "50%": { opacity: 0.7, transform: "translateY(-14px)" },
            },
          }}
        />
      ))}
      {children}
    </Box>
  );
}
